import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { getSearchSubmitError } from '../searchSubmit';
import { trackSearch, trackSearchAutocompleteSelect } from '../ga';
import './SearchBar.css';

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

function suggestionQuery(phrase) {
  return phrase.replace(/\s+$/u, '').trim();
}

/** True when the box has user text worth fetching autocomplete for. */
function hasAutocompletePrefix(query) {
  return query.replace(/^\s+/u, '').length >= 1;
}

function InsertWithoutSearchIcon() {
  return (
    <svg
      className="search-bar__suggestion-insert-icon"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden="true"
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 12 3 3M3 3v4.5M3 3h4.5"
      />
    </svg>
  );
}

export function SearchField({
  id = 'site-search',
  placeholder = 'Search products…',
  label = 'Search products',
  srLabelClass = 'sr-only',
  defaultValue = '',
  value,
  onChange,
  onSubmitSearch,
  className,
  autocompleteEnabled = true,
  scopePill = null,
  searchSubmitScope = {},
  gaSearchLocation = 'unknown',
}) {
  const isControlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(defaultValue ?? '');
  const displayValue = isControlled ? value : innerValue;
  const listboxId = useId();
  const hintId = `${id}-hint`;
  const inputRef = useRef(null);
  const blurCloseTimer = useRef(null);

  const [suggestions, setSuggestions] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [autocompleteDismissed, setAutocompleteDismissed] = useState(false);
  const [submitHint, setSubmitHint] = useState('');

  const setDisplayValue = useCallback(
    (next) => {
      if (!isControlled) {
        setInnerValue(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  const clearBlurTimer = useCallback(() => {
    if (blurCloseTimer.current != null) {
      clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
  }, []);

  const closeSuggestions = useCallback(() => {
    setSuggestions([]);
    setHighlightIndex(-1);
  }, []);

  /** Put phrase in the box with trailing space; do not run search. */
  const insertPhraseOnly = useCallback(
    (phrase) => {
      const next = `${phrase.replace(/\s+$/u, '')} `;
      setDisplayValue(next);
      closeSuggestions();
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [closeSuggestions, setDisplayValue],
  );

  const trySubmitSearch = useCallback(
    (query) => {
      const error = getSearchSubmitError(query, searchSubmitScope);
      if (error) {
        setSubmitHint(error);
        requestAnimationFrame(() => inputRef.current?.focus());
        return false;
      }
      setSubmitHint('');
      trackSearch(query, gaSearchLocation);
      onSubmitSearch?.(query);
      return true;
    },
    [gaSearchLocation, onSubmitSearch, searchSubmitScope],
  );

  /** Run search for this phrase (primary row / Enter). */
  const searchWithPhrase = useCallback(
    (phrase) => {
      const q = suggestionQuery(phrase);
      setAutocompleteDismissed(true);
      closeSuggestions();
      setDisplayValue(q);
      trackSearchAutocompleteSelect();
      trySubmitSearch(q);
    },
    [closeSuggestions, setDisplayValue, trySubmitSearch],
  );

  useEffect(() => {
    if (!autocompleteEnabled) {
      closeSuggestions();
      return;
    }
    if (!isFocused || autocompleteDismissed || !hasAutocompletePrefix(displayValue)) {
      if (!isFocused || !hasAutocompletePrefix(displayValue)) {
        closeSuggestions();
      }
      return;
    }

    const ac = new AbortController();
    const t = window.setTimeout(async () => {
      try {
        const u = new URL(
          '/api/search-autocomplete.php',
          window.location.origin,
        );
        u.searchParams.set('prefix', displayValue);
        const res = await fetch(u.toString(), { signal: ac.signal });
        if (!res.ok) {
          throw new Error('autocomplete');
        }
        const data = await res.json();
        const list = Array.isArray(data.suggestions) ? data.suggestions : [];
        if (ac.signal.aborted) {
          return;
        }
        setSuggestions(list);
        setHighlightIndex(-1);
      } catch {
        if (!ac.signal.aborted) {
          setSuggestions([]);
          setHighlightIndex(-1);
        }
      }
    }, 200);

    return () => {
      ac.abort();
      window.clearTimeout(t);
    };
  }, [autocompleteDismissed, autocompleteEnabled, closeSuggestions, displayValue, isFocused]);

  const listOpen =
    autocompleteEnabled
    && isFocused
    && !autocompleteDismissed
    && hasAutocompletePrefix(displayValue)
    && suggestions.length > 0;

  const activeDescendantId =
    listOpen && highlightIndex >= 0 && suggestions[highlightIndex]
      ? `${listboxId}-opt-${highlightIndex}`
      : undefined;

  function handleInputChange(e) {
    setAutocompleteDismissed(false);
    setSubmitHint('');
    const v = e.target.value;
    if (!isControlled) {
      setInnerValue(v);
    }
    onChange?.(v);
  }

  function handleInputKeyDown(e) {
    if (!listOpen) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => {
        if (i < 0) {
          return 0;
        }
        return Math.min(suggestions.length - 1, i + 1);
      });
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => {
        if (i <= 0) {
          return -1;
        }
        return i - 1;
      });
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setAutocompleteDismissed(true);
      closeSuggestions();
      return;
    }
    if (e.key === 'Enter' && highlightIndex >= 0) {
      const row = suggestions[highlightIndex];
      if (row?.phrase) {
        e.preventDefault();
        searchWithPhrase(row.phrase);
      }
    }
  }

  function handleInputBlur() {
    clearBlurTimer();
    blurCloseTimer.current = window.setTimeout(() => {
      setIsFocused(false);
      closeSuggestions();
    }, 150);
  }

  function handleInputFocus() {
    clearBlurTimer();
    setAutocompleteDismissed(false);
    setIsFocused(true);
  }

  function handleSuggestionMouseDown(e) {
    e.preventDefault();
    clearBlurTimer();
  }

  return (
    <form
      className={cx('search-bar', className)}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const q = (fd.get('q') ?? '').toString();
        setAutocompleteDismissed(true);
        closeSuggestions();
        trySubmitSearch(q);
      }}
    >
      <label htmlFor={id} className={cx('search-bar__label', srLabelClass)}>
        {label}
      </label>
      <div className="search-bar__combo">
        <div
          className={cx(
            'search-bar__row',
            scopePill && 'search-bar__row--has-pill',
          )}
        >
          <input
            ref={inputRef}
            id={id}
            name="q"
            type="search"
            className="search-bar__input"
            placeholder={placeholder}
            autoComplete="off"
            enterKeyHint="search"
            aria-autocomplete={autocompleteEnabled ? 'list' : undefined}
            aria-expanded={listOpen}
            aria-controls={listOpen ? listboxId : undefined}
            aria-activedescendant={activeDescendantId}
            aria-invalid={submitHint ? true : undefined}
            aria-describedby={submitHint ? hintId : undefined}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            value={displayValue}
            onChange={handleInputChange}
          />
          {scopePill && (
            <span className="search-bar__scope-pill">
              <span className="search-bar__scope-pill-label">
                {scopePill.label}
              </span>
              {typeof scopePill.onRemove === 'function' && (
                <button
                  type="button"
                  className="search-bar__scope-pill-remove"
                  aria-label={`Remove ${scopePill.label} filter`}
                  onClick={(e) => {
                    e.preventDefault();
                    scopePill.onRemove();
                  }}
                >
                  <span aria-hidden="true">×</span>
                </button>
              )}
            </span>
          )}
          <button
            type="submit"
            className="search-bar__submit"
            aria-label="Search"
          >
            Search
          </button>
        </div>
        {submitHint ? (
          <p id={hintId} className="search-bar__hint" role="alert">
            {submitHint}
          </p>
        ) : null}
        {listOpen && (
          <ul
            id={listboxId}
            className="search-bar__suggestions"
            role="listbox"
            aria-label="Search suggestions"
          >
            {suggestions.map((row, idx) => (
              <li
                key={row.phrase}
                className={cx(
                  'search-bar__suggestion-row',
                  highlightIndex === idx && 'search-bar__suggestion-row--active',
                )}
                role="presentation"
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                <button
                  type="button"
                  id={`${listboxId}-opt-${idx}`}
                  role="option"
                  aria-selected={highlightIndex === idx}
                  className="search-bar__suggestion-pick"
                  onMouseDown={handleSuggestionMouseDown}
                  onClick={() => searchWithPhrase(row.phrase)}
                >
                  <span className="search-bar__suggestion-word">{row.phrase}</span>
                </button>
                <button
                  type="button"
                  className="search-bar__suggestion-insert"
                  tabIndex={-1}
                  aria-label={`Insert “${row.phrase}” into the search box without searching`}
                  onMouseDown={handleSuggestionMouseDown}
                  onClick={(e) => {
                    e.stopPropagation();
                    insertPhraseOnly(row.phrase);
                  }}
                >
                  <InsertWithoutSearchIcon />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </form>
  );
}
